import styled from '~/app/components/styled'
import { useCallback, useMemo } from 'react'
import useModalHash from '~/app/components/common/useModalHash'
import BottomSheet from '~/app/components/common/BottomSheet/BottomSheet'
import { getYaNative } from '@yanolja-org/ya-native'
import { isNative } from '~/app/utils/typeguards'
import { DateTime } from 'luxon'
import conf from '~/app/conf'
import { Icon_help } from '@yanolja-org/ya-com-system-icon'
import { Typography } from '@yanolja-org/ya-com-core'

const Wrapper = styled.div`
  padding: 1rem 1rem 0;
`

const Notice = styled.div`
  display: flex;
  padding: 1.6rem 2rem;
  border: 0.1rem solid ${({ theme }) => theme.color.gray400};
  border-radius: 0.4rem;
  background-color: ${({ theme }) => theme.color.white};
  ${Typography('body2.multiline').regular};

  &:hover {
    @media (hover: hover) and (pointer: fine) {
      cursor: pointer;
    }
  }

  svg {
    vertical-align: middle;
    margin-left: 0.2rem;
    margin-bottom: 0.2rem;
    path {
      fill: ${({ theme }) => theme.color.gray900};
    }
  }
`

const BottomSheetWrapper = styled.div`
  padding: 1.2rem 2rem 4rem;
  display: grid;
  gap: 4rem;
`

const Title = styled.div`
  ${Typography('subtitle2').bold};
  margin-bottom: 1.2rem;
`

const List = styled.ul`
  ${Typography('body1.multiline').regular};
  display: grid;
  gap: 0.8rem;

  li {
    display: flex;
    &:before {
      display: inline-block;
      content: '•';
      margin-right: 0.6rem;
    }
  }
`

type GetIsAvailableRefundPgMobileParams = {
  createdAt?: string
  paymentPgMethod?: string
  cancelAt?: string
}

/**
 * @summary 결제수단에 따른 환불 가능 여부 조회
 * @description 결제수단 '모바일'인 경우 주문 생성월과 취소 완료월이 다르면 취소는 가능하나 자동 환불 불가로 고객센터를 통해 계좌 환불 진행 안내 문구 노출 필요
 * @link https://yanolja.atlassian.net/wiki/spaces/platform/pages/29232522/2023-09-19
 * @test test/app/components/order-cancel/CancelRefundNotice.spec.ts
 */
export const getIsAvailableRefundPg = ({
  createdAt,
  paymentPgMethod,
  cancelAt,
}: GetIsAvailableRefundPgMobileParams): boolean => {
  if (!paymentPgMethod || !cancelAt || !createdAt) {
    return true
  }

  if (paymentPgMethod !== 'MOBILE') {
    return true
  }

  const create = DateTime.fromISO(createdAt)
  const cancel = DateTime.fromISO(cancelAt)
  return cancel.hasSame(create, 'year') && cancel.hasSame(create, 'month')
}

const CancelRefundNotice = ({
  paymentPgMethod,
  cancelAt,
  createdAt,
}: GetIsAvailableRefundPgMobileParams) => {
  const [isOpened, setIsOpened] = useModalHash({ id: 'cancel-refund-notice' })

  const isShowMobilePgNotice = useMemo(
    () => !getIsAvailableRefundPg({ createdAt, paymentPgMethod, cancelAt }),
    [paymentPgMethod, cancelAt, createdAt]
  )

  const handleClickNotice = useCallback(() => {
    /**
     * 휴대폰 결제 안내 문구가 노출되는 경우 클릭시 고객센터로 이동
     */
    if (isShowMobilePgNotice) {
      const yaNative = getYaNative()
      if (isNative(yaNative)) {
        yaNative.wv({
          method: 'get',
          url: conf.customerCenter.url,
          layout: {
            title: '고객센터',
            modal: true,
          },
        })
      } else {
        window.open(conf.customerCenter.url, '_blank')
      }
      return
    }

    setIsOpened(true)
  }, [isShowMobilePgNotice])

  return (
    <>
      <Wrapper className={'noticeWrapper'}>
        <Notice onClick={handleClickNotice}>
          {isShowMobilePgNotice ? (
            <span>
              <b>휴대폰 결제 취소일이 다음달로 넘어가는 경우 계좌로 환불됩니다.</b> 자세한 내용은{' '}
              <b>고객센터</b>로 문의해 주세요.
            </span>
          ) : (
            <span>
              <b>환불은 영업일 기준 최대 7일이 걸립니다.</b> 자세한 내용은 <b>환불 정책</b>을 참고해
              주세요.
            </span>
          )}
          <Icon_help size={1.6} unit="rem" />
        </Notice>
      </Wrapper>
      <BottomSheet
        title={'환불 정책'}
        isOpened={isOpened}
        onRequestClose={() => setIsOpened(false)}
        onBackgroundClick={() => setIsOpened(false)}
      >
        <BottomSheetWrapper>
          <div>
            <Title>결제수단 별 환불 정책</Title>
            <List>
              <li>취소완료 후 원결제수단으로 취소되는 시점은 영업일 기준 3~7일이 소요됩니다.</li>
              <li>
                예약 시 선택하신 결제수단에 따라 환불이 불가능한 경우, 고객센터를 통해 계좌환불로
                대체 처리 될 수 있습니다.
              </li>
              <li>
                계좌환불 진행 시, 고객님의 계좌로 환불금액이 입금완료까지 영업일 기준 2~3일이 소요될
                수 있습니다.
              </li>
              <li>
                &apos;휴대폰 결제&apos; 예약 건은 결제 당월에 한해 원 거래 취소 및 환불 처리가
                가능합니다. 익월 이후 취소 시, 계좌를 통한 현금 또는 NOL 포인트로 환불됩니다.
              </li>
            </List>
          </div>
          <div>
            <Title>사용한 쿠폰의 반환</Title>
            <List>
              <li>
                고객 소유의 쿠폰을 사용하신 경우, 취소수수료 발생 여부와 무관하게 예약취소 시점에
                고객님 계정으로 반환됩니다.
              </li>
              <li>
                단, &apos;즉시 할인쿠폰(선착순 쿠폰)&apos;의 경우, 회원이 소유한 형태의 쿠폰이
                아니므로, 반환 대상에서 제외됩니다.
              </li>
            </List>
          </div>
        </BottomSheetWrapper>
      </BottomSheet>
    </>
  )
}

export default CancelRefundNotice
